import { CustomState } from "@iewc/Store/Reducers";
import { searchFilteringStateValues } from "@iewc/Utilities/Utilities";
import SearchFilteringTag from "@iewc/Widgets/ProductList/SearchFilteringTag";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import Button from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    const { loaded, brandFacets, productLineFacets, categoryFacets, attributeTypeFacets } =
        searchFilteringStateValues(state);

    return {
        loaded,
        searchWithin: (state.pages.productList.productFilters.searchWithinQueries ?? []).map(
            (search, idx) =>
                ({
                    count: 1,
                    id: `${idx}`,
                    name: search,
                    selected: true,
                } as FacetModel),
        ),
        brandFacets: brandFacets.filter(facet => facet.selected),
        productLineFacets: productLineFacets.filter(facet => facet.selected),
        categoryFacets: categoryFacets.filter(facet => facet.selected),
        attributeTypeFacets: attributeTypeFacets
            .map(groupFacet => ({
                ...groupFacet,
                items: groupFacet.items.filter(facet => facet.selected === true),
            }))
            .filter(groupFacet => groupFacet.items.length > 0),
    };
};

const mapDispatchToProps = {
    removeProductFilters,
    clearAllProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SearchFilteringFiltersTagsStyles {
    wrapper?: InjectableCss;
    tagsWrapper?: InjectableCss;
    clearWrapper?: InjectableCss;
    clearAllLink?: LinkPresentationProps;
    showAllWrapper?: InjectableCss;
}

export const searchFilteringFiltersTagsStyles: SearchFilteringFiltersTagsStyles = {
    wrapper: {
        css: css`
            display: flex;
            gap: 24px;
        `,
    },
    tagsWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            max-height: 88px;

            &[aria-expanded="true"] {
                max-height: none;
            }

            &[aria-expanded="false"] {
                [aria-hidden="true"] {
                    visibility: hidden;
                }
            }
        `,
    },
    clearWrapper: {
        css: css`
            margin-left: auto;
            min-width: 70px;
            text-align: right;
        `,
    },
    clearAllLink: {
        css: css`
            min-height: 40px;
        `,
    },
    showAllWrapper: {
        css: css`
            align-items: center;
            display: flex;
            justify-content: center;
            margin-top: 24px;
            text-align: center;
        `,
    },
};

const styles = searchFilteringFiltersTagsStyles;

const SearchFilteringFiltersTags = ({
    loaded,
    searchWithin,
    brandFacets,
    productLineFacets,
    categoryFacets,
    attributeTypeFacets,
    removeProductFilters,
    clearAllProductFilters,
}: Props) => {
    const [showAll, setShowAll] = useState(false);
    const [showAllWrapper, setShowAllWrapper] = useState(false);
    const tagsWrapper = useRef<HTMLDivElement>(null);

    const anyActiveFilters =
        searchWithin?.length > 0 ||
        brandFacets.length > 0 ||
        productLineFacets.length > 0 ||
        categoryFacets.length > 0 ||
        attributeTypeFacets.length > 0;

    const filtersSize =
        searchWithin.length +
        brandFacets.length +
        productLineFacets.length +
        categoryFacets.length +
        attributeTypeFacets.length;

    const clickRemoveSearchWithinHandler = (facets: FacetModel[]) => {
        removeProductFilters({ searchWithinQueries: facets.map(facet => facet.name) });
    };

    const clickRemoveBrandHandler = (facets: FacetModel[]) => {
        removeProductFilters({ brandIds: facets.map(facet => facet.id) });
    };

    const clickRemoveProductLineHandler = (facets: FacetModel[]) => {
        removeProductFilters({ productLineIds: facets.map(facet => facet.id) });
    };

    const clickRemoveCategoryHandler = (facets: FacetModel[]) => {
        removeProductFilters({ categoryId: facets[0]?.id });
    };

    const clickRemoveAttributeValueHandler = (facets: FacetModel[]) => {
        removeProductFilters({ attributeValueIds: facets.map(facet => facet.id) });
    };

    const clickShowAllHandler = () => {
        setShowAll(!showAll);
    };

    useEffect(() => {
        if ((tagsWrapper.current?.scrollHeight ?? 0) > 88) {
            setTimeout(() => {
                setShowAllWrapper(true);
            }, 0);
        } else if (showAllWrapper) {
            setTimeout(() => {
                setShowAllWrapper(false);
                setShowAll(false);
            }, 0);
        }
    }, [filtersSize, showAllWrapper]);

    if (!loaded || !anyActiveFilters) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.tagsWrapper} ref={tagsWrapper} aria-expanded={showAll}>
                    {searchWithin.length > 0 &&
                        searchWithin.map(facet => (
                            <SearchFilteringTag
                                key={`searchWithin_${facet.id}`}
                                label={facet.name}
                                size={0}
                                facetOptions={[facet]}
                                onRemoveFacet={clickRemoveSearchWithinHandler}
                                hideDropdown={true}
                                showAll={showAll}
                                tagsWrapper={tagsWrapper}
                            ></SearchFilteringTag>
                        ))}

                    {brandFacets.length > 0 && (
                        <SearchFilteringTag
                            label={translate("Brands")}
                            size={brandFacets.length}
                            facetOptions={brandFacets}
                            onRemoveFacet={clickRemoveBrandHandler}
                            showAll={showAll}
                            tagsWrapper={tagsWrapper}
                        ></SearchFilteringTag>
                    )}

                    {productLineFacets.length > 0 && (
                        <SearchFilteringTag
                            label={translate("Product Lines")}
                            size={productLineFacets.length}
                            facetOptions={productLineFacets}
                            onRemoveFacet={clickRemoveProductLineHandler}
                            showAll={showAll}
                            tagsWrapper={tagsWrapper}
                        ></SearchFilteringTag>
                    )}

                    {categoryFacets.length > 0 && (
                        <SearchFilteringTag
                            label={translate("Categories")}
                            size={categoryFacets.length}
                            facetOptions={categoryFacets}
                            onRemoveFacet={clickRemoveCategoryHandler}
                            showAll={showAll}
                            tagsWrapper={tagsWrapper}
                        ></SearchFilteringTag>
                    )}

                    {attributeTypeFacets.map(groupFacet => (
                        <SearchFilteringTag
                            key={`attributesFacet_${groupFacet.id}`}
                            label={translate(groupFacet.name)}
                            size={groupFacet.items.length}
                            facetOptions={groupFacet.items}
                            onRemoveFacet={clickRemoveAttributeValueHandler}
                            showAll={showAll}
                            tagsWrapper={tagsWrapper}
                        ></SearchFilteringTag>
                    ))}
                </StyledWrapper>

                <StyledWrapper {...styles.clearWrapper}>
                    <Link
                        {...styles.clearAllLink}
                        onClick={() => clearAllProductFilters()}
                        data-test-selector="productListClearAll"
                    >
                        {translate("Clear All")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>

            {showAllWrapper && (
                <StyledWrapper {...styles.showAllWrapper}>
                    <Button
                        variant="secondary"
                        sizeVariant="medium"
                        onClick={clickShowAllHandler}
                        data-test-selector="searchFilteringTags_ShowAll"
                    >
                        {translate(showAll ? "Hide Tags" : "Show More Tags")}
                    </Button>
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchFilteringFiltersTags as any) as any;
